import { useCursorStore } from '../stores/cursor'
import { useMachineStore } from '../stores/machine'
import { useStore } from '../stores/main'
import { useModalStore } from '../stores/modal'
import { useUserStore } from '../stores/user'
import rtcp from './rtcp'

export function useBrowser() {
  const store = useStore()
  const userStore = useUserStore()
  const machineStore = useMachineStore()
  const modalStore = useModalStore()
  const cursorStore = useCursorStore()

  function lockKeyboard() {
    if (
      'keyboard' in navigator
      || (navigator.keyboard && 'lock' in navigator.keyboard)
    ) {
      const promiseLock = navigator.keyboard?.lock()
      if (promiseLock !== undefined) {
        promiseLock
          .then((_) => {
            rtcp.inputSend({ id: 'KBLOCK', data: JSON.stringify({}) })
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return
            }

            console.error('Failed to lock keyboard:', error)
          })
      }

      return
    }

    console.error('Failed to Lock keyboard: browser not compatible.')
  }

  function unlockKeyboard() {
    if (
      'keyboard' in navigator
      || (navigator.keyboard && 'lock' in navigator.keyboard)
    ) {
      navigator.keyboard?.unlock()
      rtcp.inputSend({ id: 'KBUNLOCK', data: JSON.stringify({}) })
      rtcp.inputSend({ id: 'KEYBLUR', data: JSON.stringify({}) })
    }
  }

  function lockPointer() {
    if (store.mobile) {
      return
    }

    const contentElement = document.getElementById('content')

    if (!contentElement) {
      return
    }

    if (document.pointerLockElement !== contentElement) {
      try {
        const promise = contentElement.requestPointerLock()

        if (!promise) {
          return
        }

        promise.catch((error) => {
          console.error(error)
        })
      } catch (error) {
        console.error(error)
      }
    }
  }

  function unlockPointer() {
    const contentElement = document.getElementById('content')

    if (contentElement && document.pointerLockElement === contentElement) {
      document.exitPointerLock()
    }

    if (store.mobile) {
      cursorStore.clearAllTouches()
    }
  }

  function enterFullscreen() {
    // Check browser's actual fullscreen state instead of user preference
    // This allows entering fullscreen when preference is enabled at player startup
    // This also prevents requesting fullscreen when browser is already in fullscreen mode
    if (document.fullscreenElement || this.isStandalone()) {
      return
    }

    if (this.isIos() && machineStore.isRunningOnAndroid) {
      modalStore.open('iosFullscreenAdvices')

      return
    }

    if (document.getElementById('content')?.requestFullscreen) {
      document
        .getElementById('content')
        .requestFullscreen()
        .catch((error) => console.error(error))
    }
  }

  function exitFullscreen() {
    // Check browser's actual fullscreen state instead of user preference
    // This prevents requesting exit fullscreen when browser is not in fullscreen mode
    if (!document.fullscreenElement) {
      return
    }

    document.exitFullscreen().catch((error) => console.error(error))
  }

  function isIos() {
    return (
      [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    )
  }

  function isStandalone() {
    return (
      window.matchMedia('(display-mode: standalone)').matches
      || navigator.standalone
    )
  }

  function osName() {
    let os
    const clientStrings = [
      { s: 'WIN_10', r: /(WeytopDesktopPlayer\/Windows)/ },
      { s: 'WIN_10', r: /(Windows 10.0|Windows NT 10.0)/ },
      { s: 'WIN_8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
      { s: 'WIN_8', r: /(Windows 8|Windows NT 6.2)/ },
      { s: 'WIN_7', r: /(Windows 7|Windows NT 6.1)/ },
      { s: 'WIN_VISTA', r: /Windows NT 6.0/ },
      { s: 'WIN_VISTA', r: /Windows NT 5.2/ },
      { s: 'WIN_XP', r: /(Windows NT 5.1|Windows XP)/ },
      { s: 'WIN_10', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
      { s: 'CHROME_OS', r: /CrOS/ },
      { s: 'ANDROID', r: /Android/ },
      { s: 'LINUX', r: /(Linux|X11(?!.*CrOS))/ },
      { s: 'IPHONE', r: /iPhone/ },
      { s: 'IPAD', r: /iPad/ },
      { s: 'MACOS', r: /Mac OS X/ },
      { s: 'MACOS', r: /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
      { s: 'UNIX', r: /UNIX/ },
    ]

    for (var id in clientStrings) {
      var cs = clientStrings[id]
      if (cs.r.test(navigator.userAgent)) {
        os = cs.s
        break
      }
    }

    return os
  }

  function osDetails() {
    var r =
      /((MacPPC|MacIntel|Mac_PowerPC|Macintosh|Windows 10.0|Windows NT 10.0|Windows 8.1|Windows NT 6.3|Windows 8|Windows NT 6.2|Windows 7|Windows NT 6.1|Windows NT 6.0|Windows NT 5.2|Windows NT 5.1|Windows XP|Windows NT 4.0|WinNT4.0|WinNT|Windows NT|CrOS|Android|(Linux|X11(?!.*CrOS))|iPhone|iPad|Mac OS X|UNIX)[A-Za-z\_\.\s0-9]+\d)/
    if (r.test(navigator.userAgent)) {
      return navigator.userAgent.match(r)[0]
    }
  }

  function browserInfos() {
    let browser
    let version

    const clientStrings = [
      { s: 'CHROME', r: /Chrome\// },
      { s: 'FIREFOX', r: /Firefox\// },
      { s: 'SAFARI', r: /Safari\// },
      { s: 'OPERA', r: /Opera\// },
      { s: 'EDGE', r: /Edge\// },
    ]

    for (var id in clientStrings) {
      var cs = clientStrings[id]
      if (cs.r.test(navigator.userAgent)) {
        version = navigator.userAgent.split(cs.r)[1].split(' ')[0]
        browser = cs.s
        break
      }
    }

    return [browser, version]
  }

  function isOSCompatible() {
    const os = osName()
    let isOSCompatible = false

    if (
      os === 'MACOS'
      || os === 'IPAD'
      || os === 'IPHONE'
      || os === 'UNIX'
      || os === 'ANDROID'
      || os === 'LINUX'
      || os === 'CHROME_OS'
      || os === 'WIN_10'
    ) {
      isOSCompatible = true
    }

    return isOSCompatible
  }

  function isBrowserCompatible() {
    const infos = browserInfos()
    const browser = infos[0]
    let version = infos[1]

    let isBrowserCompatible = false
    let isVersionCompatible = false

    if (browser === 'CHROME' || browser === 'SAFARI' || browser === 'FIREFOX') {
      isBrowserCompatible = true
    }

    version = parseInt(version.match(/^([0-9]*)\.*/)[0])
    if (version) {
      if (browser === 'CHROME' && version >= 101) {
        isVersionCompatible = true
      } else if (browser === 'EDGE' && version >= 104) {
        isVersionCompatible = true
      } else if (browser === 'SAFARI' && version >= 604) {
        isVersionCompatible = true
      } else if (browser === 'OPERA' && version >= 90) {
        isVersionCompatible = true
      } else if (browser === 'FIREFOX' && version >= 121) {
        isVersionCompatible = true
      }
    }

    return isBrowserCompatible && isVersionCompatible
  }

  function isMobile() {
    var hasTouchScreen = false
    if (browserInfos()[0] === 'SAFARI' && 'maxTouchPoints' in navigator) {
      hasTouchScreen = navigator.maxTouchPoints > 0
    } else {
      ;(function (a) {
        if (
          /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
            a
          )
          || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
            a.substr(0, 4)
          )
        )
          hasTouchScreen = true
      })(navigator.userAgent || navigator.vendor || window.opera)
    }
    return hasTouchScreen
  }

  const reloadPage = () => {
    window.location.reload()
  }

  const openUrlInNewWindow = (url) => {
    window.open(url, '_blank')
  }

  return {
    lockKeyboard,
    unlockKeyboard,
    lockPointer,
    unlockPointer,
    enterFullscreen,
    exitFullscreen,
    isIos,
    isStandalone,
    isOSCompatible,
    isBrowserCompatible,
    isMobile,
    osName,
    osDetails,
    browserInfos,
    reloadPage,
    openUrlInNewWindow,
  }
}
